/**
 * LEDSettingsPanel - Configure global LED settings
 * Spec §5.10: Global LED settings (USER-EDITABLE)
 */

import { useConfigStore } from '@/store/configStore';
import { estimateCurrentDraw, calculateMaxPressedBrightness } from '@/lib/powerBudget';
import type { PowerBudgetParams } from '@/lib/powerBudget';

function usePowerBudgetParams(): PowerBudgetParams {
  const columns = useConfigStore((s) => s.columns);
  const rows = useConfigStore((s) => s.rows);
  const keysPerModule = useConfigStore((s) => s.keysPerModule);
  const globalBrightness = useConfigStore((s) => s.ledSettings.brightness);
  const idleColor = useConfigStore((s) => s.ledDefaultBehavior.idle.color);
  const idleBrightness = useConfigStore((s) => s.ledDefaultBehavior.idle.brightness);
  const pressedColor = useConfigStore((s) => s.ledDefaultBehavior.pressed.color);
  const pressedBrightness = useConfigStore((s) => s.ledDefaultBehavior.pressed.brightness);
  const powerBudget = useConfigStore((s) => s.powerBudget);
  return {
    columns, rows, keysPerModule,
    globalBrightness,
    idleColor, idleBrightness,
    pressedColor, pressedBrightness,
    maxSimultaneousPressed: powerBudget.maxSimultaneousPressed,
    converterAmps: powerBudget.converterAmps,
  };
}

export function LEDSettingsPanel() {
  const ledSettings = useConfigStore((state) => state.ledSettings);
  const setEnabled = useConfigStore((state) => state.setLEDEnabled);
  const setBrightness = useConfigStore((state) => state.setLEDBrightness);
  const setUpdateRate = useConfigStore((state) => state.setLEDUpdateRate);
  const setFadeTime = useConfigStore((state) => state.setLEDFadeTime);
  const setProfile = useConfigStore((state) => state.setLEDPerceptualProfile);
  const powerBudget = useConfigStore((state) => state.powerBudget);
  const setConverterAmps = useConfigStore((state) => state.setConverterAmps);
  const setMaxSimultaneousPressed = useConfigStore((state) => state.setMaxSimultaneousPressed);

  const budgetParams = usePowerBudgetParams();

  // Idle estimate: all keys idle (0 pressed)
  const idleParams = { ...budgetParams, maxSimultaneousPressed: 0, pressedBrightness: 0 };
  const idleCurrentMa = estimateCurrentDraw(idleParams);

  // Peak estimate: with N pressed keys at current settings
  const peakCurrentMa = estimateCurrentDraw(budgetParams);
  const budgetMa = powerBudget.converterAmps * 1000;
  const peakRatio = peakCurrentMa / budgetMa;

  const maxSafeBrightness = calculateMaxPressedBrightness(budgetParams);
  const isClamped = maxSafeBrightness < budgetParams.pressedBrightness;

  const statusColor = peakRatio > 1.0 ? '#dc3545' : peakRatio > 0.8 ? '#ffc107' : '#28a745';
  const statusLabel = peakRatio > 1.0 ? 'OVER BUDGET' : peakRatio > 0.8 ? 'Near limit' : 'OK';

  return (
    <>
      {/* Enable/Disable - Spec §5.10 */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={ledSettings.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <span>Enable LEDs</span>
        </label>
      </div>

      {/* Global Brightness - Spec §5.10: 0.0-1.0 */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="brightness"
          style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
        >
          Global Brightness: {ledSettings.brightness.toFixed(2)}
        </label>
        <input
          id="brightness"
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={ledSettings.brightness}
          onChange={(e) => setBrightness(parseFloat(e.target.value))}
          disabled={!ledSettings.enabled}
          style={{
            width: '100%',
            cursor: ledSettings.enabled ? 'pointer' : 'not-allowed',
          }}
        />
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
          Master dimming (0.0-1.0)
        </div>
      </div>

      {/* Update Rate - Spec §5.10: 30-200 Hz */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="update-rate"
          style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
        >
          Update Rate: {ledSettings.update_rate_hz} Hz
        </label>
        <input
          id="update-rate"
          type="range"
          min="30"
          max="200"
          step="10"
          value={ledSettings.update_rate_hz}
          onChange={(e) => setUpdateRate(parseInt(e.target.value, 10))}
          disabled={!ledSettings.enabled}
          style={{
            width: '100%',
            cursor: ledSettings.enabled ? 'pointer' : 'not-allowed',
          }}
        />
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
          LED refresh frequency (30-200 Hz)
        </div>
      </div>

      {/* Fade Time - 0-2000ms (protocol max 2550ms) */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="fade-time"
          style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
        >
          Default Fade Time: {ledSettings.fade_default_ms} ms
        </label>
        <input
          id="fade-time"
          type="range"
          min="0"
          max="2000"
          step="50"
          value={ledSettings.fade_default_ms}
          onChange={(e) => setFadeTime(parseInt(e.target.value, 10))}
          disabled={!ledSettings.enabled}
          style={{
            width: '100%',
            cursor: ledSettings.enabled ? 'pointer' : 'not-allowed',
          }}
        />
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
          Color transition time (0=instant, 150-300=smooth, 500+=slow)
        </div>
      </div>

      {/* Perceptual Profile - Spec §5.10: default | soft | vivid */}
      <div style={{ marginBottom: '12px' }}>
        <label
          htmlFor="perceptual-profile"
          style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
        >
          Perceptual Profile:
        </label>
        <select
          id="perceptual-profile"
          value={ledSettings.perceptual_profile ?? 'default'}
          onChange={(e) => {
            const value = e.target.value as 'default' | 'soft' | 'vivid';
            setProfile(value);
          }}
          disabled={!ledSettings.enabled}
          style={{
            width: '100%',
            padding: '6px',
            fontSize: '14px',
            border: '1px solid #e9ecef',
            borderRadius: '4px',
            cursor: ledSettings.enabled ? 'pointer' : 'not-allowed',
            backgroundColor: '#fff',
          }}
        >
          <option value="default">Default (gamma 2.2)</option>
          <option value="soft">Soft (gamma 2.4 - bright ambient)</option>
          <option value="vivid">Vivid (gamma 2.0 - dark ambient)</option>
        </select>
        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
          Color perception adjustment
        </div>
      </div>

      {/* Power Budget Section */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        backgroundColor: '#f8f9fa',
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold' }}>
          Power Budget
        </h4>

        {/* Converter capacity */}
        <div style={{ marginBottom: '10px' }}>
          <label
            htmlFor="converter-amps"
            style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}
          >
            Converter capacity (A):
          </label>
          <input
            id="converter-amps"
            type="number"
            min="0.5"
            max="20"
            step="0.5"
            value={powerBudget.converterAmps}
            onChange={(e) => setConverterAmps(parseFloat(e.target.value) || 3.0)}
            style={{
              width: '80px',
              padding: '4px 6px',
              fontSize: '13px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
            }}
          />
          <span style={{ fontSize: '11px', color: '#6c757d', marginLeft: '8px' }}>
            TPSM53603 = 3A
          </span>
        </div>

        {/* Max simultaneous pressed keys */}
        <div style={{ marginBottom: '12px' }}>
          <label
            htmlFor="max-pressed"
            style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}
          >
            Max simultaneous pressed keys:
          </label>
          <input
            id="max-pressed"
            type="number"
            min="1"
            max="192"
            step="1"
            value={powerBudget.maxSimultaneousPressed}
            onChange={(e) => setMaxSimultaneousPressed(parseInt(e.target.value, 10) || 10)}
            style={{
              width: '80px',
              padding: '4px 6px',
              fontSize: '13px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
            }}
          />
        </div>

        {/* Current estimates */}
        <div style={{ fontSize: '13px', marginBottom: '4px' }}>
          Idle current: <strong>{Math.round(idleCurrentMa)} mA</strong>
        </div>
        <div style={{ fontSize: '13px', marginBottom: '8px' }}>
          Peak current ({budgetParams.maxSimultaneousPressed} pressed):{' '}
          <strong style={{ color: statusColor }}>{Math.round(peakCurrentMa)} mA</strong>
          {' / '}
          {budgetMa} mA
          <span style={{
            marginLeft: '8px',
            padding: '1px 6px',
            borderRadius: '3px',
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: statusColor,
          }}>
            {statusLabel}
          </span>
        </div>

        {isClamped && (
          <div style={{
            padding: '6px 8px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#856404',
          }}>
            Pressed brightness auto-limited to {maxSafeBrightness.toFixed(2)} in exported config
          </div>
        )}
      </div>

      {/* Informational note - Spec §5.10 NORMATIVE */}
      <div
        style={{
          marginTop: '12px',
          padding: '10px 12px',
          backgroundColor: '#e9ecef',
          borderRadius: '6px',
          fontSize: '11px',
          color: '#6c757d',
        }}
      >
        Note: Gamma correction is FIXED at 2.2 (spec §5.10). Perceptual profile
        is an optional modifier.
      </div>
    </>
  );
}
